import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { TRIFFIN_CREDIT_PRODUCT_TERMS } from "@viable/funding/client";
import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { usePortalContainer } from "../../shared/portal-container-context";

export interface CreditCalculatorProps {
	className?: string;
	currency?: string;
	annualRevenue?: number;
	avgInventoryOrderValue?: number;
	repaymentType?: "monthly" | "bullet";
	repaymentMonths?: number;
	// eslint-disable-next-line no-unused-vars -- Parameter name required by TypeScript function type syntax
	onApply?: (arg: {
		currency: string;
		revenue: number;
		inventory: number;
		months: number;
		facility: number;
		drawdown: number;
		totalFee: number;
		totalPayable: number;
		repayments: number;
		aprPercent: number;
		percentOfRevenue: number;
	}) => void;
}

function clamp(n: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, n));
}

function parseMoneyInput(value: string): number {
	if (typeof value !== "string") return 0;
	const cleaned = value.replace(/[^\d.]/g, "");
	const parsed = Number(cleaned);
	return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(amount: number, currency: string): string {
	const safe = Number.isFinite(amount) ? amount : 0;
	try {
		return new Intl.NumberFormat("en-GB", {
			style: "currency",
			currency: currency || "GBP",
			maximumFractionDigits: 0,
		}).format(safe);
	} catch {
		return `£${Math.round(safe).toLocaleString("en-GB")}`;
	}
}

function formatPercent(p: number): string {
	const safe = Number.isFinite(p) ? p : 0;
	return `${safe.toFixed(1)}%`;
}

function RepaymentMonthsSlider({
	value,
	onChange,
	min = 2,
	max = 6,
	step = 1,
}: {
	value: number;
	// eslint-disable-next-line no-unused-vars -- Parameter name required by TypeScript function type syntax
	onChange: (arg: number) => void;
	min?: number;
	max?: number;
	step?: number;
}) {
	return (
		<SliderPrimitive.Root
			value={[value]}
			onValueChange={([v]) => onChange(v)}
			min={min}
			max={max}
			step={step}
			className="relative flex w-full touch-none select-none items-center"
			aria-label="Repayment period slider"
		>
			<SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-none bg-sage-600">
				<SliderPrimitive.Range className="absolute h-full bg-green-500" />
			</SliderPrimitive.Track>
			<SliderPrimitive.Thumb className="block h-6 w-6 rounded-full border-[3px] border-green-500 bg-white shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-green-500/25" />
		</SliderPrimitive.Root>
	);
}

function ChevronDownIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
			<path
				d="M5 7.5L10 12.5L15 7.5"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function CheckIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
			<path
				d="M16.25 5.75L8.75 13.25L3.75 8.25"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function CurrencySelect({
	value,
	onChange,
}: {
	value: string;
	// eslint-disable-next-line no-unused-vars -- Parameter name required by TypeScript function type syntax
	onChange: (arg: string) => void;
}) {
	const portalContainer = usePortalContainer();
	return (
		<SelectPrimitive.Root value={value} onValueChange={onChange}>
			<SelectPrimitive.Trigger
				className="h-auto py-2.5 px-3 text-xs border border-grey-700 bg-white rounded-none w-auto min-w-[84px] inline-flex items-center justify-between gap-2 text-teal-500"
				aria-label="Currency"
			>
				<SelectPrimitive.Value />
				<SelectPrimitive.Icon asChild>
					<ChevronDownIcon className="h-4 w-4 text-teal-500" />
				</SelectPrimitive.Icon>
			</SelectPrimitive.Trigger>

			<SelectPrimitive.Portal container={portalContainer ?? undefined}>
				<SelectPrimitive.Content
					position="popper"
					sideOffset={6}
					className="z-[9999] bg-white border border-grey-700 rounded-none shadow-none overflow-hidden min-w-[var(--radix-select-trigger-width)]"
				>
					<SelectPrimitive.Viewport className="p-1">
						<SelectPrimitive.Item
							value="GBP"
							className="relative flex w-full cursor-pointer select-none items-center py-2 pl-3 pr-9 text-sm text-teal-500 outline-none data-[highlighted]:bg-grey-500 data-[state=checked]:font-semibold"
						>
							<SelectPrimitive.ItemText>🇬🇧 GBP</SelectPrimitive.ItemText>
							<SelectPrimitive.ItemIndicator className="absolute right-3 inline-flex items-center justify-center text-teal-500">
								<CheckIcon className="h-4 w-4" />
							</SelectPrimitive.ItemIndicator>
						</SelectPrimitive.Item>
					</SelectPrimitive.Viewport>
				</SelectPrimitive.Content>
			</SelectPrimitive.Portal>
		</SelectPrimitive.Root>
	);
}

function RepaymentMonthsSelect({
	value,
	onChange,
	options,
}: {
	value: number;
	// eslint-disable-next-line no-unused-vars -- Parameter name required by TypeScript function type syntax
	onChange: (arg: number) => void;
	options: number[];
}) {
	const portalContainer = usePortalContainer();
	return (
		<SelectPrimitive.Root value={value.toString()} onValueChange={(v) => onChange(Number(v))}>
			<SelectPrimitive.Trigger
				className="h-auto py-2.5 px-3 text-xs border border-grey-700 bg-white rounded-none w-auto min-w-[100px] inline-flex items-center justify-between gap-2 text-teal-500"
				aria-label="Repayment period months"
			>
				<SelectPrimitive.Value />
				<SelectPrimitive.Icon asChild>
					<ChevronDownIcon className="h-4 w-4 text-teal-500" />
				</SelectPrimitive.Icon>
			</SelectPrimitive.Trigger>

			<SelectPrimitive.Portal container={portalContainer ?? undefined}>
				<SelectPrimitive.Content
					position="popper"
					sideOffset={6}
					className="z-[9999] bg-white border border-grey-700 rounded-none shadow-none overflow-hidden min-w-[var(--radix-select-trigger-width)]"
				>
					<SelectPrimitive.Viewport className="p-1">
						{options.map((m) => (
							<SelectPrimitive.Item
								key={m}
								value={m.toString()}
								className="relative flex w-full cursor-pointer select-none items-center py-2 pl-3 pr-9 text-sm text-teal-500 outline-none data-[highlighted]:bg-grey-500 data-[state=checked]:font-semibold"
							>
								<SelectPrimitive.ItemText>{`${m} months`}</SelectPrimitive.ItemText>
								<SelectPrimitive.ItemIndicator className="absolute right-3 inline-flex items-center justify-center text-teal-500">
									<CheckIcon className="h-4 w-4" />
								</SelectPrimitive.ItemIndicator>
							</SelectPrimitive.Item>
						))}
					</SelectPrimitive.Viewport>
				</SelectPrimitive.Content>
			</SelectPrimitive.Portal>
		</SelectPrimitive.Root>
	);
}

function FacilityProgressBar({ value }: { value: number }) {
	const safe = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
	return (
		<ProgressPrimitive.Root
			value={safe}
			className="relative w-full overflow-hidden rounded-none bg-sage-600 h-1.5 mb-2.5"
			aria-label="Estimated credit facility percentage"
		>
			<ProgressPrimitive.Indicator
				className="h-full w-full bg-green-500 transition-transform duration-300 ease-in-out"
				style={{ transform: `translateX(-${100 - safe}%)` }}
			/>
		</ProgressPrimitive.Root>
	);
}

export function CreditCalculator({
	className,
	currency: initialCurrency = "GBP",
	annualRevenue: initialAnnualRevenue = 1200000,
	avgInventoryOrderValue: initialAvgInventoryOrderValue = 25000,
	repaymentType: initialRepaymentType = "monthly",
	repaymentMonths: initialRepaymentMonths = 4,
	onApply,
}: CreditCalculatorProps) {
	const [currency, setCurrency] = useState(initialCurrency);
	const [annualRevenue, setAnnualRevenue] = useState(initialAnnualRevenue);
	const [avgInventoryOrderValue, setAvgInventoryOrderValue] = useState(initialAvgInventoryOrderValue);
	const [repaymentType, setRepaymentType] = useState<"monthly" | "bullet">(initialRepaymentType);
	const [repaymentMonths, setRepaymentMonths] = useState(initialRepaymentMonths);
	const [annualRevenueDisplay, setAnnualRevenueDisplay] = useState(() =>
		Math.max(0, initialAnnualRevenue || 0).toLocaleString("en-GB"),
	);
	const [avgInventoryDisplay, setAvgInventoryDisplay] = useState(() =>
		Math.max(0, initialAvgInventoryOrderValue || 0).toLocaleString("en-GB"),
	);
	const [isAnnualFocused, setIsAnnualFocused] = useState(false);
	const [isInventoryFocused, setIsInventoryFocused] = useState(false);
	const annualInputRef = useRef<HTMLInputElement>(null);
	const inventoryInputRef = useRef<HTMLInputElement>(null);

	const currencySymbol = currency === "GBP" ? "£" : "";

	function caretPosFromDigitIndex(formatted: string, digitIndex: number): number {
		if (digitIndex <= 0) return 0;
		let digitsSeen = 0;
		for (let i = 0; i < formatted.length; i += 1) {
			if (/\d/.test(formatted[i] ?? "")) {
				digitsSeen += 1;
				if (digitsSeen >= digitIndex) return i + 1;
			}
		}
		return formatted.length;
	}

	function updateMoneyInput({
		raw,
		caretStart,
		prefix,
		max,
		setNumeric,
		setDisplay,
		ref,
	}: {
		raw: string;
		caretStart: number | null;
		max: number;
		setNumeric: (n: number) => void;
		setDisplay: (s: string) => void;
		ref: React.RefObject<HTMLInputElement | null>;
		prefix: string;
	}) {
		const digitsLeft = raw.slice(0, caretStart ?? raw.length).replace(/\D/g, "").length;
		const digits = raw.replace(/\D/g, "");
		const parsed = digits ? Number(digits) : 0;
		const clamped = clamp(parsed, 0, max);

		setNumeric(clamped);

		const formatted = digits.length === 0 ? "" : clamped.toLocaleString("en-GB");
		const nextDisplay = formatted ? `${prefix}${formatted}` : "";
		setDisplay(nextDisplay);

		// Restore caret after React updates the controlled value.
		requestAnimationFrame(() => {
			const el = ref.current;
			if (!el) return;
			const totalDigits = nextDisplay.replace(/\D/g, "").length;
			const targetDigitIndex = Math.min(digitsLeft, totalDigits);
			const prefixLen = nextDisplay.startsWith(prefix) ? prefix.length : 0;
			const pos = Math.max(prefixLen, caretPosFromDigitIndex(nextDisplay, targetDigitIndex));
			try {
				el.setSelectionRange(pos, pos);
			} catch {
				// Ignore (e.g. input type not supporting selection)
			}
		});
	}

	// Keep in sync with props
	useEffect(() => {
		if (initialCurrency) setCurrency(initialCurrency);
		if (Number.isFinite(initialAnnualRevenue)) setAnnualRevenue(initialAnnualRevenue);
		if (Number.isFinite(initialAvgInventoryOrderValue)) setAvgInventoryOrderValue(initialAvgInventoryOrderValue);
		if (initialRepaymentType) setRepaymentType(initialRepaymentType);
		if (Number.isFinite(initialRepaymentMonths)) setRepaymentMonths(initialRepaymentMonths);
	}, [
		initialCurrency,
		initialAnnualRevenue,
		initialAvgInventoryOrderValue,
		initialRepaymentType,
		initialRepaymentMonths,
	]);

	const revenueStep = 100000;
	const inventoryStep = 5000;

	const derived = useMemo(() => {
		const revenue = clamp(annualRevenue, 0, 1_000_000_000);
		const inventory = clamp(avgInventoryOrderValue, 0, 100_000_000);
		const months = clamp(repaymentMonths, 2, 6);

		// Facility estimate: 12.5% of annual revenue, capped at product maximum
		const facility = Math.min(Math.max(0, revenue * 0.125), TRIFFIN_CREDIT_PRODUCT_TERMS.MAX_CREDIT_LIMIT);

		// Typical drawdown estimate
		const drawdown = clamp(inventory, 0, facility);

		const monthlyFeeRate = 0.015; // 2% per month
		const bulletFeeRateTotal = 0.02; // 1.5% total

		const feeRate = repaymentType === "bullet" ? bulletFeeRateTotal : monthlyFeeRate;

		const totalFee = repaymentType === "bullet" ? drawdown * feeRate * months : drawdown * feeRate * months;

		console.log("months", drawdown);
		console.log("drawdown", drawdown);
		console.log("feeRate", feeRate);
		console.log("totalFee", totalFee);
		console.log("drawdown", drawdown);
		const totalPayable = drawdown + totalFee;
		const repayments = repaymentType === "bullet" ? totalPayable : totalPayable / months;

		const percentOfRevenue = revenue > 0 ? (facility / revenue) * 100 : 0;

		return {
			revenue,
			inventory,
			months,
			facility,
			drawdown,
			totalFee,
			totalPayable,
			repayments,
			aprPercent: feeRate * 100,
			percentOfRevenue,
		};
	}, [annualRevenue, avgInventoryOrderValue, repaymentMonths, repaymentType]);

	const progressPct = clamp(derived.percentOfRevenue, 0, 25); // UI cap at 25%
	const progressBarValue = (progressPct / 25) * 100; // Radix Progress expects 0-100

	// Keep display values in sync with numeric state, but don't clobber mid-typing.
	useEffect(() => {
		if (!isAnnualFocused) {
			const formatted = Math.max(0, annualRevenue || 0).toLocaleString("en-GB");
			setAnnualRevenueDisplay(formatted ? `${currencySymbol}${formatted}` : "");
		}
	}, [annualRevenue, isAnnualFocused, currencySymbol]);
	useEffect(() => {
		if (!isInventoryFocused) {
			const formatted = Math.max(0, avgInventoryOrderValue || 0).toLocaleString("en-GB");
			setAvgInventoryDisplay(formatted ? `${currencySymbol}${formatted}` : "");
		}
	}, [avgInventoryOrderValue, isInventoryFocused, currencySymbol]);

	function RepaymentCard({
		id,
		title,
		description,
		selected,
		onSelect,
	}: {
		id: "monthly" | "bullet";
		title: string;
		description: string;
		selected: boolean;
		// eslint-disable-next-line no-unused-vars -- Parameter name required by TypeScript function type syntax
		onSelect: (arg: "monthly" | "bullet") => void;
	}) {
		return (
			<button
				type="button"
				className={cn(
					"border rounded-none p-3 text-left cursor-pointer bg-white transition-colors",
					selected ? "cc-border-teal-500 shadow-none" : "cc-border-grey-700 hover:cc-border-grey-800",
				)}
				onClick={() => onSelect(id)}
				aria-pressed={selected}
			>
				<div className="flex gap-2.5 items-start">
					<div
						className={cn(
							"w-[18px] h-[18px] border rounded-none flex items-center justify-center flex-none mt-0.5 relative bg-white",
							selected && "cc-border-teal-500 bg-teal-500 text-white",
						)}
						aria-hidden="true"
					>
						{selected && <CheckIcon className="h-4 w-4" />}
					</div>
					<div>
						<div className={cn("text-teal-500 text-sm", selected && "font-semibold")}>{title}</div>
						<div className="text-foreground-tertiary text-xs mt-1 leading-[1.35] ">{description}</div>
					</div>
				</div>
			</button>
		);
	}

	return (
		<div className={cn("max-w-[1100px] mx-auto p-4 min-[992px]:p-6 font-sans text-teal-500", className)}>
			<div className="text-[32px] min-[992px]:text-[40px] leading-[1.1] font-medium text-teal-500 my-1.5 mb-6">
				Unlock your business potential with Triffin Credit
			</div>

			<div className="flex flex-col min-[992px]:flex-row items-stretch gap-0   justify-center">
				{/* Left Panel */}
				<div className="flex-1 border cc-border-grey-700 bg-white flex flex-col cc-left-panel">
					<div className="cc-border-grey-600 h-[104px] border-b border-grey-600">
						<div className="  px-8 py-4 ">
							<div className=" text-teal-500 text-2xl font-semibold">
								Estimate your credit facility size
							</div>
							<div className=" text-[13px] text-foreground-tertiary">
								Your credit facility size is based on your annual revenue
							</div>
						</div>
					</div>
					{/* Annual revenue */}
					<div className="pt-4 pb-8 px-8 flex flex-col  border-b cc-border-grey-600">
						<div className="flex items-start justify-between gap-3 mb-2.5">
							<div>
								<div className=" text-teal-500 text-lg ">Enter your annual revenue</div>
							</div>
							<div>
								<CurrencySelect value={currency} onChange={setCurrency} />
							</div>
						</div>
						<div className="flex gap-0">
							<Input
								ref={annualInputRef}
								className="flex-1 border border-grey-600 border-r-0 rounded-none px-3.5 py-3 text-sm text-teal-500 outline-none focus:border-green-500 focus:ring-0 focus:ring-offset-0 focus:ring-green-500/18"
								classNameWrapper="w-full"
								value={annualRevenueDisplay}
								onFocus={() => setIsAnnualFocused(true)}
								onChange={(e) =>
									updateMoneyInput({
										raw: e.target.value,
										caretStart: e.target.selectionStart,
										max: 1_000_000_000,
										setNumeric: (n) => setAnnualRevenue(n),
										setDisplay: setAnnualRevenueDisplay,
										ref: annualInputRef,
										prefix: currencySymbol,
									})
								}
								onBlur={() => {
									setIsAnnualFocused(false);
									const formatted = Math.max(0, annualRevenue || 0).toLocaleString("en-GB");
									setAnnualRevenueDisplay(formatted ? `${currencySymbol}${formatted}` : "");
								}}
								inputMode="numeric"
								aria-label="Annual revenue"
							/>
							<div className="flex">
								<button
									type="button"
									className="w-11 border border-grey-700 bg-grey-500 text-teal-500 text-lg cursor-pointer rounded-none hover:opacity-60"
									onClick={() => {
										const next = clamp(annualRevenue - revenueStep, 0, 1_000_000_000);
										setAnnualRevenue(next);
										setAnnualRevenueDisplay(`${currencySymbol}${next.toLocaleString("en-GB")}`);
									}}
									aria-label="Decrease Annual revenue"
								>
									–
								</button>
								<button
									type="button"
									className="w-11 border border-grey-700 border-l-0 bg-grey-500 text-teal-500 text-lg cursor-pointer rounded-none hover:opacity-60"
									onClick={() => {
										const next = clamp(annualRevenue + revenueStep, 0, 1_000_000_000);
										setAnnualRevenue(next);
										setAnnualRevenueDisplay(`${currencySymbol}${next.toLocaleString("en-GB")}`);
									}}
									aria-label="Increase Annual revenue"
								>
									+
								</button>
							</div>
						</div>
					</div>

					<div className="p-5.5 bg-sage-400 border-b border-t border-sage-400 flex-1 flex flex-col justify-center">
						<div className="text-teal-500">Your estimated credit facility size</div>
						<div className="text-[64px] tracking-[-1px] font-semibold my-3 mb-1.5 text-teal-500">
							{formatCurrency(derived.facility, currency)}
						</div>
						<div className="text-teal-500 text-[13px] mb-4 font-normal">
							Your actual credit facility size may differ depending on growth rate and profitability
						</div>

						{/* <div className="border cc-border-sage-600 rounded-none pb-3.5 pt-0">
							<FacilityProgressBar value={progressPct} />

							<div className="text-teal-500 text-[13px] font-medium px-3.5">
								{formatPercent(derived.percentOfRevenue)} of your annual revenue
							</div>
						</div> */}

						{/* <Button
							type="button"
							className="w-[150px] mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all text-sm  h-10 px-8 border-none rounded-none cursor-pointer bg-green-500 text-teal-500 hover:bg-green-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500/30 focus-visible:ring-offset-0 focus-visible:ring-offset-green-500/15"
							onClick={() => onApply?.({ ...derived, currency })}
						>
							Apply for funding
						</Button> */}
					</div>
				</div>

				{/* Right Panel */}
				<div className="flex-1 bg-white border cc-border-grey-700 cc-right-panel">
					<div className="px-8 py-4 h-[104px] border-b border-grey-600">
						<div className=" text-teal-500 text-2xl font-semibold">Estimate your financing costs</div>
						<div className=" text-[13px] text-foreground-tertiary">
							You're only charged when you use your facility to finance B2B sales invoices or inventory orders
						</div>
					</div>
					<div className="  cc-border-grey-600 bg-white">
						<div className="py-4 px-8">
							{/* Inventory order value */}
							<div className="pb-4">
								<div className="flex items-start justify-between gap-3 mb-2.5">
									<div>
										<div className=" text-teal-500 text-md">
											Enter your B2B invoice or inventory order value
										</div>
									</div>
									<div>
										<CurrencySelect value={currency} onChange={setCurrency} />
									</div>
								</div>
								<div className="flex gap-0">
									<Input
										ref={inventoryInputRef}
										className="flex-1 border border-grey-600 border-r-0 rounded-none px-3.5 py-3 text-sm text-teal-500 outline-none focus:border-green-500 focus:ring-0 focus:ring-offset-0 focus:ring-green-500/18"
										classNameWrapper="w-full"
										value={avgInventoryDisplay}
										onFocus={() => setIsInventoryFocused(true)}
										onChange={(e) =>
											updateMoneyInput({
												raw: e.target.value,
												caretStart: e.target.selectionStart,
												max: 100_000_000,
												setNumeric: (n) => setAvgInventoryOrderValue(n),
												setDisplay: setAvgInventoryDisplay,
												ref: inventoryInputRef,
												prefix: currencySymbol,
											})
										}
										onBlur={() => {
											setIsInventoryFocused(false);
											const formatted = Math.max(0, avgInventoryOrderValue || 0).toLocaleString("en-GB");
											setAvgInventoryDisplay(formatted ? `${currencySymbol}${formatted}` : "");
										}}
										inputMode="numeric"
										aria-label="Average inventory order value"
									/>
									<div className="flex">
										<button
											type="button"
											className="w-11 border border-grey-700 bg-grey-500 text-teal-500 text-lg cursor-pointer rounded-none hover:opacity-60"
											onClick={() => {
												const next = clamp(avgInventoryOrderValue - inventoryStep, 0, 100_000_000);
												setAvgInventoryOrderValue(next);
												setAvgInventoryDisplay(`${currencySymbol}${next.toLocaleString("en-GB")}`);
											}}
											aria-label="Decrease Average inventory order value"
										>
											–
										</button>
										<button
											type="button"
											className="w-11 border border-grey-700 border-l-0 bg-grey-500 text-teal-500 text-lg cursor-pointer rounded-none hover:opacity-60"
											onClick={() => {
												const next = clamp(avgInventoryOrderValue + inventoryStep, 0, 100_000_000);
												setAvgInventoryOrderValue(next);
												setAvgInventoryDisplay(`${currencySymbol}${next.toLocaleString("en-GB")}`);
											}}
											aria-label="Increase Average inventory order value"
										>
											+
										</button>
									</div>
								</div>
							</div>
							<div className=" text-teal-500 text-base my-0.5 text-lg">Select a repayment type:</div>
							<div className=" text-[13px] text-foreground-tertiary mb-2">
								When you draw funds from your credit facility, you can choose between two repayment options and you can
								repay over 2-6 months
							</div>
							<div className="grid grid-cols-1 min-[992px]:grid-cols-2 gap-3">
								<RepaymentCard
									id="monthly"
									title="Monthly repayments:"
									description="Pay equal amounts in monthly instalments at a fee starting from 1.5% per month"
									selected={repaymentType === "monthly"}
									onSelect={setRepaymentType}
								/>
								<RepaymentCard
									id="bullet"
									title="Bullet repayment:"
									description="You pay one lump sum at the end of the period with a fee starting of 2% per month"
									selected={repaymentType === "bullet"}
									onSelect={setRepaymentType}
								/>
							</div>
						</div>

						<div className="py-4 px-8">
							<div className="flex items-center justify-between gap-3 mb-3">
								<div className=" text-teal-500 text-lg  my-0.5">Select repayment period:</div>
								<RepaymentMonthsSelect value={derived.months} onChange={setRepaymentMonths} options={[2, 3, 4, 5, 6]} />
							</div>

							<div className="my-3 mb-2.5 px-1" style={{ minHeight: "24px", height: "24px" }}>
								<RepaymentMonthsSlider value={derived.months} onChange={setRepaymentMonths} />
							</div>

							<div className="flex justify-between text-grey-1000 text-xs font-medium" aria-hidden="true">
								{[2, 3, 4, 5, 6].map((m) => (
									<div key={m} className={m === derived.months ? "text-teal-500 font-semibold" : ""}>
										{`${m} months`}
									</div>
								))}
							</div>
						</div>
						{/* <div className="grid grid-cols-1 min-[992px]:grid-cols-3 border-b cc-border-grey-600">
							<div className="p-6 px-7 cc-desktop-border-r cc-border-grey-600 flex flex-col justify-start gap-2.5 border-b cc-desktop-border-b-0 whitespace-nowrap">
								<div className="text-teal-500 text-[13px] mb-0">
									{repaymentType === "bullet" ? "Number of repayments" : "Repayments"}
								</div>
								<div className="text-teal-500 text-[20px]  ">
									{repaymentType === "bullet" ? "1" : formatCurrency(derived.repayments, currency)}
								</div>
							</div>
							<div className="p-6 px-7 cc-desktop-border-r cc-border-grey-600 flex flex-col justify-start gap-2.5 border-b cc-desktop-border-b-0 whitespace-nowrap">
								<div className="text-teal-500 text-[13px] mb-0">Total payable</div>
								<div className="text-teal-500 text-[20px]  ">
									{formatCurrency(derived.totalPayable, currency)}
								</div>
							</div>
							<div className="p-6 px-7 flex flex-col justify-start gap-2.5 whitespace-nowrap">
								<div className="text-teal-500 text-[13px] mb-0">Fixed fee %</div>
								<div className="text-teal-500 text-[20px]  ">{derived.aprPercent.toFixed(1)}%</div>
							</div>
						</div> */}

						<div className="p-8 px-7 bg-purple-500 flex flex-col justify-center min-h-[120px]">
							{/* <div className="text-teal-500 mb-1.5 text-lg">Estimated cost of drawdown</div> */}
							<div className="text-foreground-primary leading-[1.4] text-xl  ">
								{repaymentType === "bullet" ? (
									<>
										You'll pay{" "}
										<span className="font-semibold text-teal-500">
											{formatCurrency(derived.totalPayable, currency)}
										</span>{" "}
										at the end of{" "}
										<span className="font-semibold text-teal-500">{derived.months} months</span> with a
										total fee of{" "}
										<span className="font-semibold text-teal-500">
											{formatCurrency(derived.totalFee, currency)}
										</span>
										.
									</>
								) : (
									<>
										You'll pay{" "}
										<span className="font-semibold text-teal-500">
											{formatCurrency(derived.repayments, currency)}
										</span>{" "}
										per month for{" "}
										<span className="font-semibold text-teal-500">{derived.months} months</span> with a
										total fee of{" "}
										<span className="font-semibold text-teal-500">
											{formatCurrency(derived.totalFee, currency)}
										</span>
										.
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="flex justify-center mt-5.5">
				<Button
					type="button"
					className="inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all text-sm  h-10 px-10 border-none rounded-none cursor-pointer bg-green-500 text-teal-500 hover:bg-green-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500/30 focus-visible:ring-offset-0 focus-visible:ring-offset-green-500/15"
				>
					Apply for Triffin Credit
				</Button>
			</div>
		</div>
	);
}
